import { Dispatch, Fragment, SetStateAction, useCallback, useState } from "react";
import { Box, Checkbox, FormControlLabel, Typography, useTheme } from "@mui/material";
import { Responses } from "@/components/Quiz/useQuiz";
import useRanking from "@/components/Quiz/Ranking/useRanking";
import { Trans } from "@lingui/macro";
import RankItem from "@/components/Quiz/Ranking/RankItem";
import { AnswerListPossibility } from "@WahooFitness/cloud-client-types";
import {
  useSensors,
  TouchSensor,
  MouseSensor,
  closestCenter,
  DndContext,
  useSensor,
  DragOverlay,
  DragStartEvent,
} from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import SortableItem from "@/components/SortableItem";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { SENSOR_CONFIG } from "@/hooks/useDragAndDrop";
import { motion } from "motion/react";

interface Props {
  initialVal?: Array<string>;
  options: Array<AnswerListPossibility>;
  skipText?: string;
  setResponses: Dispatch<SetStateAction<Responses | undefined>>;
  questionId: number;
}

const Ranking = ({ initialVal, options, setResponses, questionId, skipText }: Props) => {
  const [activeId, setActiveId] = useState<string | null>(null);

  const { onDragEnd, orderedOptions, skipChecked, handleSkipToggle } = useRanking({
    initialVal,
    options,
    setResponses,
    questionId,
    setActiveId,
  });

  const { palette } = useTheme();

  const sensors = useSensors(
    useSensor(TouchSensor, SENSOR_CONFIG),
    useSensor(MouseSensor, SENSOR_CONFIG)
  );

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  }, []);

  return (
    <Box
      height="100%"
      width="100%"
      maxWidth="sm"
      py={1}
      display="flex"
      flexDirection="column"
      gap={1.5}
    >
      <Typography color={skipChecked ? palette.text.disabled : "textSecondary"}>
        <Trans>Drag and drop items in preferred order.</Trans>
      </Typography>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={onDragEnd}
        modifiers={[restrictToVerticalAxis]}
      >
        <SortableContext
          items={orderedOptions.map((item) => item.key)}
          strategy={verticalListSortingStrategy}
        >
          {orderedOptions.map((option) => (
            <Fragment key={option.key}>
              <SortableItem key={option.key} id={option.key}>
                <Box>
                  <RankItem text={option.text.title} disabled={skipChecked} />
                </Box>
              </SortableItem>
              <DragOverlay dropAnimation={null}>
                {activeId === option.key && (
                  <motion.div
                    style={{ borderRadius: 12 }}
                    initial={{ scale: 1 }}
                    animate={{
                      scale: 1.05,
                      boxShadow:
                        "0 3px 14px 2px rgba(0, 0, 0, 0.08), 0 8px 10px 1px rgba(0, 0, 0, 0.08), 0 5px 5px -3px rgba(0, 0, 0, 0.12)",
                    }}
                    transition={{ type: "spring", duration: 0.2 }}
                  >
                    <RankItem text={option.text.title} disabled={skipChecked} />
                  </motion.div>
                )}
              </DragOverlay>
            </Fragment>
          ))}
        </SortableContext>
      </DndContext>
      {skipText && (
        <Box mx={2}>
          <FormControlLabel
            control={<Checkbox checked={skipChecked} onChange={handleSkipToggle} />}
            label={skipText}
          />
        </Box>
      )}
    </Box>
  );
};

export default Ranking;
